<script setup lang="ts">
import { Check } from 'lucide-vue-next'
import { useSignupStorage } from '@/composables/useSignupStorage'

const { signup } = useSignupStorage()

defineEmits<{
  (e: 'back'): void
  (e: 'next'): void
}>()

const options = [
  { value: 'front_and_back', label: 'Front & Back' },
  { value: 'front_only', label: 'Front Only' },
  { value: 'back_only', label: 'Back Only' },
] as const
</script>

<template>
  <div>
    <div class="mb-[61px]">
      <label class="text-xs font-bold uppercase tracking-[1.4px] text-mm-dark">
        Areas to mow<span class="text-mm-green">*</span>
      </label>

      <div class="mt-4 rounded-xl border border-gray-200 px-4 py-6 sm:px-[55px] sm:py-[52px] shadow-sm">
        <div class="flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-center sm:gap-[21px]">
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          class="cursor-pointer flex flex-row items-center gap-4 rounded-xl border-2 p-4 transition-colors sm:flex-col sm:border-0 sm:p-0"
          :class="signup.mow_areas === option.value ? 'border-mm-green bg-mm-green/5 sm:bg-transparent' : 'border-gray-200'"
          @click="signup.mow_areas = option.value"
        >
          <!-- Front & Back illustration (from mow_both.svg) -->
          <svg v-if="option.value === 'front_and_back'" viewBox="0 0 204 204" fill="none" xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" class="size-[80px] shrink-0 sm:size-[200px]">
            <rect x="1" y="1" width="202" height="202" rx="17" fill="white" :stroke="signup.mow_areas === option.value ? '#2CAB33' : '#E0E0E0'" stroke-width="2" />
            <rect x="13.5" y="13.5" width="177" height="177" rx="9.5" fill="#2CAB33" fill-opacity="0.3" stroke="#2CAB33" />
            <g filter="url(#filter0_dii_134_813)">
              <path d="M78 88C78 82.4772 82.4772 78 88 78H116C121.523 78 126 82.4772 126 88V116C126 121.523 121.523 126 116 126H88C82.4772 126 78 121.523 78 116V88Z" fill="white" />
              <path d="M88 78.5H116C121.247 78.5 125.5 82.7533 125.5 88V116C125.5 121.247 121.247 125.5 116 125.5H88C82.7533 125.5 78.5 121.247 78.5 116V88C78.5 82.7533 82.7533 78.5 88 78.5Z" stroke="#2CAB33" />
              <path d="M99 112V102H105V112M93 99L102 92L111 99V110C111 110.53 110.789 111.039 110.414 111.414C110.039 111.789 109.53 112 109 112H95C94.4696 112 93.9609 111.789 93.5858 111.414C93.2107 111.039 93 110.53 93 110V99Z" stroke="#414651" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </g>
            <defs>
              <filter id="filter0_dii_134_813" x="76" y="77" width="52" height="52" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="1" />
                <feGaussianBlur stdDeviation="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_134_813" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_134_813" result="shape" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="-2" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="shape" result="effect2_innerShadow_134_813" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect3_innerShadow_134_813" />
                <feOffset />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.18 0" />
                <feBlend mode="normal" in2="effect2_innerShadow_134_813" result="effect3_innerShadow_134_813" />
              </filter>
            </defs>
          </svg>

          <!-- Front Only illustration (from mow_front.svg) -->
          <svg v-if="option.value === 'front_only'" viewBox="0 0 202 202" fill="none" xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" class="size-[80px] shrink-0 sm:size-[200px]">
            <rect x="0.5" y="0.5" width="201" height="201" rx="16.5" fill="white" :stroke="signup.mow_areas === option.value ? '#2CAB33' : '#E0E0E0'" />
            <rect x="12.5" y="100.5" width="177" height="89" rx="9.5" fill="#2CAB33" fill-opacity="0.3" stroke="#2CAB33" />
            <g filter="url(#filter0_dii_134_815)">
              <path d="M77 87C77 81.4772 81.4772 77 87 77H115C120.523 77 125 81.4772 125 87V115C125 120.523 120.523 125 115 125H87C81.4772 125 77 120.523 77 115V87Z" fill="white" />
              <path d="M87 77.5H115C120.247 77.5 124.5 81.7533 124.5 87V115C124.5 120.247 120.247 124.5 115 124.5H87C81.7533 124.5 77.5 120.247 77.5 115V87C77.5 81.7533 81.7533 77.5 87 77.5Z" stroke="#2CAB33" />
              <path d="M98 111V101H104V111M92 98L101 91L110 98V109C110 109.53 109.789 110.039 109.414 110.414C109.039 110.789 108.53 111 108 111H94C93.4696 111 92.9609 110.789 92.5858 110.414C92.2107 110.039 92 109.53 92 109V98Z" stroke="#414651" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </g>
            <defs>
              <filter id="filter0_dii_134_815" x="75" y="76" width="52" height="52" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="1" />
                <feGaussianBlur stdDeviation="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_134_815" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_134_815" result="shape" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="-2" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="shape" result="effect2_innerShadow_134_815" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect3_innerShadow_134_815" />
                <feOffset />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.18 0" />
                <feBlend mode="normal" in2="effect2_innerShadow_134_815" result="effect3_innerShadow_134_815" />
              </filter>
            </defs>
          </svg>

          <!-- Back Only illustration (from mow_back.svg) -->
          <svg v-if="option.value === 'back_only'" viewBox="0 0 202 202" fill="none" xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" class="size-[80px] shrink-0 sm:size-[200px]">
            <rect x="0.5" y="0.5" width="201" height="201" rx="16.5" fill="white" :stroke="signup.mow_areas === option.value ? '#2CAB33' : '#E0E0E0'" />
            <rect x="12.5" y="12.5" width="177" height="89" rx="9.5" fill="#2CAB33" fill-opacity="0.3" stroke="#2CAB33" />
            <g filter="url(#filter0_dii_134_816)">
              <path d="M77 87C77 81.4772 81.4772 77 87 77H115C120.523 77 125 81.4772 125 87V115C125 120.523 120.523 125 115 125H87C81.4772 125 77 120.523 77 115V87Z" fill="white" />
              <path d="M87 77.5H115C120.247 77.5 124.5 81.7533 124.5 87V115C124.5 120.247 120.247 124.5 115 124.5H87C81.7533 124.5 77.5 120.247 77.5 115V87C77.5 81.7533 81.7533 77.5 87 77.5Z" stroke="#2CAB33" />
              <path d="M98 111V101H104V111M92 98L101 91L110 98V109C110 109.53 109.789 110.039 109.414 110.414C109.039 110.789 108.53 111 108 111H94C93.4696 111 92.9609 110.789 92.5858 110.414C92.2107 110.039 92 109.53 92 109V98Z" stroke="#414651" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </g>
            <defs>
              <filter id="filter0_dii_134_816" x="75" y="76" width="52" height="52" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="1" />
                <feGaussianBlur stdDeviation="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_134_816" />
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_134_816" result="shape" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feOffset dy="-2" />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.05 0" />
                <feBlend mode="normal" in2="shape" result="effect2_innerShadow_134_816" />
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                <feMorphology radius="1" operator="erode" in="SourceAlpha" result="effect3_innerShadow_134_816" />
                <feOffset />
                <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0496732 0 0 0 0 0.0705882 0 0 0 0.18 0" />
                <feBlend mode="normal" in2="effect2_innerShadow_134_816" result="effect3_innerShadow_134_816" />
              </filter>
            </defs>
          </svg>

          <div class="flex items-center gap-2 sm:mt-3 sm:justify-center">
            <span
              class="flex size-4.5 shrink-0 items-center justify-center rounded border-2 transition-colors"
              :class="signup.mow_areas === option.value
                ? 'border-mm-green bg-mm-green'
                : 'border-gray-300 bg-white'"
            >
              <Check v-if="signup.mow_areas === option.value" class="size-3 text-white" />
            </span>
            <span class="text-base font-semibold sm:text-sm sm:font-medium text-mm-dark">{{ option.label }}</span>
          </div>
        </button>
        </div>

        <div class="mt-6 border-t border-gray-200 pt-6">
          <p class="text-sm font-semibold text-mm-dark">Is your property a corner lot?<span class="text-mm-green">*</span></p>
          <div class="mt-3 space-y-1">
            <label class="flex cursor-pointer items-center gap-3">
              <input
                type="radio"
                name="corner_lot"
                value="yes"
                v-model="signup.corner_lot"
                class="peer sr-only"
              />
              <span
                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
              >
                <svg
                  v-if="signup.corner_lot === 'yes'"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="size-3 text-white"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span class="text-sm">Yes</span>
            </label>
            <label class="flex cursor-pointer items-center gap-3">
              <input
                type="radio"
                name="corner_lot"
                value="no"
                v-model="signup.corner_lot"
                class="peer sr-only"
              />
              <span
                class="flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-input shadow-xs peer-checked:border-mm-green peer-checked:bg-mm-green"
              >
                <svg
                  v-if="signup.corner_lot === 'no'"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="size-3 text-white"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
              <span class="text-sm">No</span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-center gap-4">
      <button
        type="button"
        class="cursor-pointer rounded-md border-2 border-gray-300 px-[36.5px] py-[14.6px] font-bold uppercase tracking-[1.6px] text-[14px] text-mm-dark transition-colors hover:border-gray-400"
        @click="$emit('back')"
      >
        Back
      </button>
      <button
        type="button"
        class="cursor-pointer rounded-md bg-mm-green px-[36.5px] py-[14.6px] font-bold uppercase tracking-[1.6px] text-[14px] text-white transition-colors hover:bg-mm-green-dark disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!signup.corner_lot"
        @click="$emit('next')"
      >
        Next
      </button>
    </div>
  </div>
</template>
